import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAws, faMicrosoft, faGoogle } from '@fortawesome/free-brands-svg-icons';

const TechnologiesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
  
  @media (max-width: 576px) {
    padding: 50px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 576px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }
  
  &:before {
    left: -40px;
  }
  
  &:after {
    right: -40px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    letter-spacing: 1px;
    
    &:before, &:after {
      width: 20px;
    }
    
    &:before {
      left: -25px;
    }
    
    &:after {
      right: -25px;
    }
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 576px) {
    font-size: 15px;
    max-width: 100%;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const TabButton = styled.button`
  background: ${props => props.active ? '#ff6e6e' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#333'};
  border: 2px solid ${props => props.active ? '#ff6e6e' : '#ddd'};
  border-radius: 30px;
  padding: 10px 25px;
  font-size: 16px;
  font-weight: 600;
  margin: 0 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  
  &:hover {
    background: ${props => props.active ? '#ff6e6e' : '#f0f0f0'};
    border-color: ${props => props.active ? '#ff6e6e' : '#ccc'};
  }
  
  @media (max-width: 576px) {
    padding: 8px 20px;
    font-size: 14px;
    margin: 0 5px;
  }
`;

const TechGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TechItem = styled(motion.div)`
  background-color: #fff;
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    padding: 25px 15px;
  }
`;

const TechIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
  color: #ff6e6e;
  display: flex;
  justify-content: center;
  align-items: center;
  
  svg {
    font-size: 40px;
    
    @media (max-width: 576px) {
      font-size: 36px;
    }
  }
  
  @media (max-width: 576px) {
    font-size: 36px;
    margin-bottom: 15px;
  }
`;

const TechName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 576px) {
    font-size: 18px;
  }
`;

const TechDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  
  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const Technologies = () => {
  const [activeTab, setActiveTab] = useState(1);
  
  const techCategories = [
    [
      {
        name: 'React.js',
        icon: '⚛️',
        description: 'Building interactive user interfaces with the latest React features and hooks.'
      },
      {
        name: 'Node.js',
        icon: '🟢',
        description: 'Server-side JavaScript runtime for building scalable backend applications.'
      },
      {
        name: 'Python',
        icon: '🐍',
        description: 'Versatile programming language for data science, AI, and backend development.'
      },
      {
        name: 'React Native',
        icon: '📱',
        description: 'Framework for building native mobile applications using React.'
      },
      {
        name: 'MongoDB',
        icon: '🍃',
        description: 'NoSQL database for flexible and scalable data storage solutions.'
      },
      {
        name: 'NuxtJS',
        icon: '🟩',
        description: 'Intuitive Vue framework for building modern web applications with server-side rendering.'
      },
      {
        name: 'GraphQL',
        icon: '◼️',
        description: 'Query language for APIs that provides a more efficient alternative to REST.'
      },
      {
        name: 'PHP',
        icon: '🐘',
        description: 'Popular server-side scripting language for web development and content management systems.'
      }
    ],
    [
      {
        name: 'Google Cloud',
        icon: <FontAwesomeIcon icon={faGoogle} style={{ color: '#4285F4' }} />,
        description: 'Suite of cloud computing services for building, deploying, and scaling applications.'
      },
      {
        name: 'AWS',
        icon: <FontAwesomeIcon icon={faAws} style={{ color: '#FF9900' }} />,
        description: 'Comprehensive cloud platform offering over 200 fully featured services from data centers globally.'
      },
      {
        name: 'Azure',
        icon: <FontAwesomeIcon icon={faMicrosoft} style={{ color: '#0078D4' }} />,
        description: 'Microsoft\'s cloud computing service for building, testing, deploying, and managing applications.'
      },
      {
        name: 'Ubuntu',
        icon: '🐧',
        description: 'Popular Linux distribution known for its ease of use and regular release updates.'
      },
      {
        name: 'Docker',
        icon: '🐳',
        description: 'Platform for developing, shipping, and running applications in containers.'
      },
      {
        name: 'TensorFlow',
        icon: '🧠',
        description: 'Open-source platform for machine learning and artificial intelligence applications.'
      },
      {
        name: 'WHM',
        icon: '🔧',
        description: 'Web Host Manager for server administration and website management.'
      },
      {
        name: 'CPanel',
        icon: '🎛️',
        description: 'Web hosting control panel that provides a graphical interface for managing websites.'
      }
    ]
  ];
  
  const handleTabChange = (index) => {
    setActiveTab(index);
  };
  
  const tabVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, y: -20 }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };
  
  return (
    <TechnologiesSection id="technologies">
      <Container>
        <SectionTitle>
          <SubTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Our Tech Stack
          </SubTitle>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Technologies We <span>Excel In</span>
          </Title>
          <Description
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            We leverage cutting-edge technologies to deliver robust, scalable, and efficient solutions for our clients. Our expertise spans across various domains of software development.
          </Description>
        </SectionTitle>
        
        <TabsContainer>
          <TabButton 
            active={activeTab === 0} 
            onClick={() => handleTabChange(0)}
          >
            Frontend & Backend
          </TabButton>
          <TabButton 
            active={activeTab === 1} 
            onClick={() => handleTabChange(1)}
          >
            Cloud & Infrastructure
          </TabButton>
        </TabsContainer>
        
        <AnimatePresence mode="wait">
          <TechGrid
            key={activeTab}
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {techCategories[activeTab].map((tech, index) => (
              <TechItem
                key={index}
                variants={itemVariants}
              >
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechItem>
            ))}
          </TechGrid>
        </AnimatePresence>
      </Container>
    </TechnologiesSection>
  );
};

export default Technologies;